import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { FormattedMessage } from 'react-intl'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'

const ImageContainer = styled(Image)`
  height: ${rem(260)};
  ${mediaQueryUp.tablet} {
    height: ${rem(320)};
  }
  ${mediaQueryUp.desktop} {
    height: ${rem(480)};
  }
`
function TripLandingPageWhyYoullLoveIt() {
  return (
    <Sheet padding="64 0" background="neutral-eight" paletteType="inverse">
      <LayoutContainer>
        <Group direction="vertical" gap={40} horizontalAlign="center">
          <Heading variant="heading2">
            <FormattedMessage
              id="akpCCwx-B-QxRXphA8mS"
              defaultMessage="Why you’ll <i>love it</i>"
              description="Main heading for why you'll love it section in the trip planner"
              values={{
                i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
              }}
            />
          </Heading>
          <Group direction="vertical" gap={64}>
            <Group direction="vertical" gap={24} desktopDirection="horizontal" horizontalAlign="center">
              <ImageContainer
                id="qcetphs298psne6mi4g"
                enhancedFormats={false}
                height={480}
              />
              <Sheet desktopPadding="68 40" padding="0 20">
                <Group direction="vertical" gap={16} desktopGap={20} horizontalAlign="start">
                  <Group direction="vertical" gap={12}>
                    <Heading variant="heading2">
                      <FormattedMessage
                        id="Lu0PDMVJmrKo29uBksQO"
                        defaultMessage="Itinerary planning, done all for you"
                        description="Heading for itinerary planning section on the itinerary or schedule view"
                      />
                    </Heading>
                    <BodyText variant="large">
                      <FormattedMessage
                        id="NQ6StW1xHvK2nWshPnO5"
                        defaultMessage="No more starting from scratch. Just tell us what kind of trip you’re after, and TripPlanner instantly builds a personalised itinerary, crafted to match your interests, pace, and travel style.{br}{br}From where to stay to what to eat, see, and do, everything is covered for you, effortlessly."
                        description="Description for itinerary planning section in the trip planner"
                        values={{
                          br: <br />,
                        }}
                      />
                    </BodyText>
                  </Group>
                  <TextButton kind="secondary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to={CHAT_TRIP_PLANNER_NEW_PATH} fit="mobile-full-width">
                    <FormattedMessage
                      id="dySWPALQI8IpmcGX9MqJ"
                      defaultMessage="Start planning - It’s free"
                      description="Call-to-action button text to start planning a new trip in the trip planner tool"
                    />
                  </TextButton>
                </Group>
              </Sheet>
            </Group>
            <Group direction="vertical" gap={24} desktopDirection="horizontal" horizontalAlign="center">
              <CSSBreakpoint max="tablet">
                <ImageContainer
                  id="e33ubeatjqhrjswefxcm"
                  enhancedFormats={false}
                  height={480}
                />
              </CSSBreakpoint>
              <Sheet desktopPadding="68 40" padding="0 20">
                <Group direction="vertical" gap={16} desktopGap={20} horizontalAlign="start">
                  <Group direction="vertical" gap={12}>
                    <Heading variant="heading2">
                      <FormattedMessage
                        id="t7xj76SxNpZr6bCXAOyC"
                        defaultMessage="Dream, plan & book in one place"
                        description="Section heading for dream, plan and book section in the trip planner"
                      />
                    </Heading>
                    <BodyText variant="large">
                      <FormattedMessage
                        id="I6j4Z0aZvLTcOh_js0wX"
                        defaultMessage="TripPlanner lets you save ideas for later, plan when you’re ready, and book when the time is right. From flights, hotels, tours, cruises, car hire and local experiences, you can browse and book every item directly within your itinerary.{br}{br}No more juggling apps, tabs or tools. Just one seamless space to bring your trip to life."
                        description="Description for dream, plan and book section in the cruise section"
                        values={{
                          br: <br />,
                        }}
                      />
                    </BodyText>
                  </Group>
                  <TextButton kind="secondary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to={CHAT_TRIP_PLANNER_NEW_PATH} fit="mobile-full-width">
                    <FormattedMessage
                      id="dySWPALQI8IpmcGX9MqJ"
                      defaultMessage="Start planning - It’s free"
                      description="Call-to-action button text to start planning a new trip in the trip planner tool"
                    />
                  </TextButton>
                </Group>
              </Sheet>
              <CSSBreakpoint min="desktop">
                <ImageContainer
                  id="e33ubeatjqhrjswefxcm"
                  enhancedFormats={false}
                  height={480}
                />
              </CSSBreakpoint>
            </Group>
            <Group direction="vertical" gap={24} desktopDirection="horizontal" horizontalAlign="center">
              <ImageContainer
                id="viwdp341xm5wzfmgez7"
                enhancedFormats={false}
                height={480}
              />
              <Sheet desktopPadding="68 40" padding="0 20">
                <Group direction="vertical" gap={16} desktopGap={20} horizontalAlign="start">
                  <Group direction="vertical" gap={12}>

                    <Heading variant="heading2">
                      <FormattedMessage
                        id="74ZGEDW_zP0YgXN9wfSU"
                        defaultMessage="Booked elsewhere? No problem"
                        description="Section heading for booked elsewhere section in the trip planner"
                      />
                    </Heading>
                    <BodyText variant="large">
                      <FormattedMessage
                        id="V7Ki7nK7WCwyNadDIGqh"
                        defaultMessage="Simply forward any email to TripPlanner and it will automatically add it to your itinerary and organise it into one seamless timeline.{br}{br}You’ll never need to search your inbox for booking confirmations again!"
                        description="Description for booked elsewhere section in the trip planner"
                        values={{
                          br: <br />,
                        }}
                      />
                    </BodyText>
                  </Group>
                  <TextButton kind="secondary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to={CHAT_TRIP_PLANNER_NEW_PATH} fit="mobile-full-width">
                    <FormattedMessage
                      id="dySWPALQI8IpmcGX9MqJ"
                      defaultMessage="Start planning - It’s free"
                      description="Call-to-action button text to start planning a new trip in the trip planner tool"
                    />
                  </TextButton>
                </Group>
              </Sheet>
            </Group>
          </Group>
          <Group direction="vertical" gap={24} desktopDirection="horizontal" horizontalAlign="center">
            <CSSBreakpoint max="tablet">
              <ImageContainer
                id="yibrz2eoyvzbmayj1s3g"
                enhancedFormats={false}
                height={480}
              />
            </CSSBreakpoint>
            <Sheet desktopPadding="68 40" padding="0 20">
              <Group direction="vertical" gap={16} desktopGap={20} horizontalAlign="start">
                <Group direction="vertical" gap={12}>
                  <Heading variant="heading2">
                    <FormattedMessage
                      id="proBMCZqI6Kc9KYNI7xO"
                      defaultMessage="Change of plans? No worries"
                      description="Section heading for change of plans section in the trip planner"
                    />
                  </Heading>
                  <BodyText variant="large">
                    <FormattedMessage
                      id="nMWt-PKw2qz2nMC0MWH3"
                      defaultMessage="TripPlanner makes it easy to edit on the go. Rearrange your day, move bookings, or regenerate your plan with a click.{br}{br}It’s the flexibility you need, without the frustration."
                      description="Description for change of plans section in the trip planner feature"
                      values={{
                        br: <br />,
                      }}
                    />
                  </BodyText>
                </Group>
                <TextButton kind="secondary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to={CHAT_TRIP_PLANNER_NEW_PATH} fit="mobile-full-width">
                  <FormattedMessage
                    id="dySWPALQI8IpmcGX9MqJ"
                    defaultMessage="Start planning - It’s free"
                    description="Call-to-action button text to start planning a new trip in the trip planner tool"
                  />
                </TextButton>
              </Group>
            </Sheet>
            <CSSBreakpoint min="desktop">
              <ImageContainer
                id="yibrz2eoyvzbmayj1s3g"
                enhancedFormats={false}
                height={480}
              />
            </CSSBreakpoint>
          </Group>
          <Group direction="vertical" gap={24} desktopDirection="horizontal" horizontalAlign="center">
            <ImageContainer
              id="rnsx6dl5vnwy4na60i5u"
              enhancedFormats={false}
              height={480}
            />
            <Sheet desktopPadding="68 40" padding="0 20">
              <Group direction="vertical" gap={16} desktopGap={20} horizontalAlign="start">
                <Group direction="vertical" gap={12}>
                  <Heading variant="heading2">
                    <FormattedMessage
                      id="dlKFfqIlLZxpCPgzU6k9"
                      defaultMessage="Plan together, stay in sync"
                      description="Section heading for plan together section in the trip planner"
                    />
                  </Heading>
                  <BodyText variant="large">
                    <FormattedMessage
                      id="3UGKFoeO7EMYitMHDq40"
                      defaultMessage="Invite others to view or edit your trip. Everyone can collaborate in real time, see the latest plans, and share the excitement without the messy group chats."
                      description="Description for plan together section in the trip planner feature"
                    />
                  </BodyText>
                </Group>
                <TextButton kind="secondary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to={CHAT_TRIP_PLANNER_NEW_PATH} fit="mobile-full-width">
                  <FormattedMessage
                    id="dySWPALQI8IpmcGX9MqJ"
                    defaultMessage="Start planning - It’s free"
                    description="Call-to-action button text to start planning a new trip in the trip planner tool"
                  />
                </TextButton>
              </Group>
            </Sheet>
          </Group>

        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default TripLandingPageWhyYoullLoveIt
